import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductListTabsRefs
 * @property {HTMLElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 */

/** @extends {Component<ProductListTabsRefs>} */
export class ProductListTabsComponent extends Component {
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { tabs, panels } = this.refs;
    if (!tabs?.length || !panels?.length) return;

    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );

    this.#updateActiveTab();
    this.#setupEventListeners();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', (e) => this.#handleTabClick(e, i), opts);
    }
  }

  #handleKeydown(/** @type {KeyboardEvent} */ e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs) return;

    const i = tabs.indexOf(target);
    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -i,
      End: tabs.length - 1 - i,
    };

    const offset = navMap[e.key];
    if (offset !== undefined) {
      e.preventDefault();
      const nextIndex = Math.max(0, Math.min(i + offset, tabs.length - 1));
      tabs[nextIndex]?.focus();
      this.#activate(nextIndex);
    }
  }

  #handleTabClick(/** @type {MouseEvent} */ e, /** @type {number} */ index) {
    e.preventDefault();
    this.#activate(index);
  }

  /**
   * @param {number} index
   */
  #activate(index) {
    const { tabs } = this.refs;
    if (!tabs || index === this.#active || index < 0 || index >= tabs.length) return;

    this.#active = index;
    this.#updateActiveTab();
  }

  #updateActiveTab() {
    const { tabs, panels } = this.refs;

    for (const [i, tab] of tabs?.entries() ?? []) {
      const isActive = i === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels?.entries() ?? []) {
      const isActive = i === this.#active;
      panel.toggleAttribute('hidden', !isActive);
      panel.setAttribute('tabindex', isActive ? '0' : '-1');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }
}

customElements.define('product-list-tabs-component', ProductListTabsComponent);
